import React, { useState, useCallback } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { useApp } from './context/AppContext';
import ChangePasswordPrompt from './components/ChangePasswordPrompt';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Clients from './pages/ClientsV2';  // Updated to V2 table layout
import ClientDetail from './pages/ClientDetail';
import Employees from './pages/Employees';
import EmployeeDetail from './pages/EmployeeDetail';
import Estimates from './pages/Estimates';
import EstimateNew from './pages/EstimateNew';
import EstimateDetail from './pages/EstimateDetail';
import EstimateEdit from './pages/EstimateEdit';
import EstimateSchedule from './pages/EstimateSchedule';
import Dispatch from './pages/Dispatch';
import TeamAvailability from './pages/TeamAvailability';
import QuickAddJob from './pages/QuickAddJob';
import Settings from './pages/Settings';
import TeamApp from './pages/TeamApp';
import ClientPortal from './pages/ClientPortal';
import ClientPortalMain from './pages/ClientPortalMain';
import QuoteRequest from './pages/QuoteRequest';
import LaborAnalysis from './pages/LaborAnalysis';
import Invoices from './pages/Invoices';
import InvoiceDetail from './pages/InvoiceDetail';
import InvoiceNew from './pages/InvoiceNew';
import TipsReport from './pages/TipsReport';
import Reports from './pages/Reports';
import CalendarView from './pages/CalendarView';
import Messages from './pages/Messages';
import StaffMessages from './pages/StaffMessages';
import CustomEstimate from './pages/CustomEstimate';
import OnSiteAppointments from './pages/OnSiteAppointments';
import TimeCardReport from './pages/TimeCardReport';
import GustoReport from './pages/GustoReport';
import Todos from './pages/Todos';
import Quizzes from './pages/Quizzes';
import AttendancePoints from './pages/AttendancePoints';
import ComplaintsFeedback from './pages/ComplaintsFeedback';
import ScopeOfService from './pages/ScopeOfService';
import CommercialServices from './pages/CommercialServices';
import TermsAndConditions from './pages/TermsAndConditions';
import PhotoWaiver from './pages/PhotoWaiver';
import PropertyManagerPortal from './pages/PropertyManagerPortal';
import InvoicePayment from './pages/InvoicePayment';
import OwnerDashboard from './pages/OwnerDashboard';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import Expenses from './pages/Expenses';
import Accounts from './pages/Accounts';
import ExpenseCategories from './pages/ExpenseCategories';
import CashflowReport from './pages/CashflowReport';
import BankStatementImport from './pages/BankStatementImport';
// Onboarding module (Phase 1)
import ChecklistView from './modules/onboarding/components/ChecklistView';
import ClickToAgree from './modules/onboarding/components/ClickToAgree';
import DocumentUpload from './modules/onboarding/components/DocumentUpload';
import NewHireOnboardingForm from './modules/onboarding/components/NewHireOnboardingForm';
import AssignOnboardingToEmployee from './modules/onboarding/components/AssignOnboardingToEmployee';
import AssignPoliciesToEmployee from './modules/onboarding/components/AssignPoliciesToEmployee';
import ManagerOnboardingDashboard from './modules/onboarding/components/ManagerOnboardingDashboard';
import ManagerEmployeeOnboardingView from './modules/onboarding/components/ManagerEmployeeOnboardingView';
import EmployeeOnboardingArchive from './modules/onboarding/components/EmployeeOnboardingArchive';
import PolicyTemplateManager from './modules/onboarding/components/PolicyTemplateManager';
import PolicyTemplateEdit from './modules/onboarding/components/PolicyTemplateEdit';
// Leads is now integrated into Estimates page

// Redirect helper for dynamic routes
function ClientDetailRedirect() {
  const { id } = useParams();
  return <Navigate to={`/admin/clients/${id}`} replace />;
}

function EstimateDetailRedirect() {
  const { id } = useParams();
  return <Navigate to={`/admin/estimates/${id}`} replace />;
}

function InvoiceDetailRedirect() {
  const { id } = useParams();
  return <Navigate to={`/admin/invoices/${id}`} replace />;
}

// Owner emails - business owners get enhanced reporting access
const OWNER_EMAILS = [
  'sarah@twosisterscleaningco.com',
  'sarah.keel@twosisterscleaningco.com',
];

// Admin emails - these users get full admin access
const ADMIN_EMAILS = [
  'sarah@twosisterscleaningco.com',
  'sarah.keel@twosisterscleaningco.com',
  'elaine@twosisterscleaningco.com',
  'sam@twosisterscleaningco.com',
];

function App() {
  const { user, isAuthenticated, loading } = useAuth();
  const { state, updateEmployee } = useApp();
  const [passwordChanged, setPasswordChanged] = useState(false);

  // Determine user role based on email
  const getUserRole = () => {
    if (!user?.email) return null;

    // Check if admin by email
    if (ADMIN_EMAILS.includes(user.email.toLowerCase())) {
      return 'admin';
    }

    // Check if employee
    const employee = state.employees.find(
      emp => emp.email?.toLowerCase() === user.email.toLowerCase()
    );

    if (employee) {
      // Admin/owner roles get full admin access
      const adminRoles = ['admin', 'owner'];
      if (adminRoles.includes(employee.role)) {
        return 'admin';
      }
      // Manager gets admin section access but with restricted views
      if (employee.role === 'manager') {
        return 'manager';
      }
      return employee.role === 'lead' ? 'lead' : 'employee';
    }

    // Default to admin if no employee match (for initial setup)
    return 'admin';
  };

  const userRole = getUserRole();
  const isAdmin = userRole === 'admin';
  const isEmployee = userRole === 'employee' || userRole === 'lead';
  const isOwner = user?.email && OWNER_EMAILS.includes(user.email.toLowerCase());

  // Owner-only routes (financials, payroll, accounting, owner dashboard)
  const ownerGate = (element) => isOwner ? element : <Navigate to="/admin/dashboard" replace />;
  // Admin+owner routes (block managers from analytics and reports)
  const adminGate = (element) => (isAdmin || isOwner) ? element : <Navigate to="/admin/dashboard" replace />;

  // Check if user needs to change password (first login with temp password)
  const currentEmployee = isAuthenticated && user?.email
    ? state.employees.find(emp => emp.email?.toLowerCase() === user.email.toLowerCase())
    : null;
  const mustChangePassword = currentEmployee?.mustChangePassword && !passwordChanged;

  const handlePasswordChanged = useCallback(async () => {
    if (currentEmployee) {
      await updateEmployee(currentEmployee.id, { mustChangePassword: false });
    }
    setPasswordChanged(true);
  }, [currentEmployee, updateEmployee]);

  // Show loading while checking auth state
  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner-large" />
        <p>Loading...</p>
      </div>
    );
  }

  // Show password change prompt if required
  if (isAuthenticated && mustChangePassword) {
    return <ChangePasswordPrompt onComplete={handlePasswordChanged} />;
  }

  return (
    <Routes>
      {/* Public Landing Page */}
      <Route path="/" element={<LandingPage />} />
      
      {/* Employee/Admin Login */}
      <Route
        path="/login"
        element={
          isAuthenticated
            ? <Navigate to={
                currentEmployee?.status === 'onboarding' ? "/onboarding"
                  : isEmployee && !isAdmin ? "/team"
                  : isOwner ? "/admin/owner-dashboard"
                  : "/admin/dashboard"
              } replace />
            : <Login />
        }
      />
      
      {/* Client Portal (public - full portal with login) */}
      <Route path="/portal" element={<ClientPortalMain />} />
      
      {/* Quote Request Form (public - intake form) */}
      <Route path="/quote" element={<QuoteRequest />} />

      {/* Commercial Services (public - Keel Commercial info page) */}
      <Route path="/commercial" element={<CommercialServices />} />

      {/* Standard Scope of Service (public - shareable) */}
      <Route path="/scope" element={<ScopeOfService />} />

      {/* Terms and Conditions (public - shareable) */}
      <Route path="/terms" element={<TermsAndConditions />} />

      {/* Photo Waiver Form (public - client opts out of before/after photos) */}
      <Route path="/photo-waiver" element={<PhotoWaiver />} />

      {/* Property Manager Portal (public - standalone portal) */}
      <Route path="/property" element={<PropertyManagerPortal />} />
      
      {/* Client Portal - Estimate approval (uses token auth) */}
      <Route path="/portal/approve/:token" element={<ClientPortal />} />
      
      {/* Client Portal - Direct access with token */}
      <Route path="/portal/:token" element={<ClientPortalMain />} />

      {/* Direct Invoice Payment (no login required) */}
      <Route path="/pay/:token" element={<InvoicePayment />} />

      {/* Team App (for employees) */}
      <Route
        path="/team"
        element={
          <ProtectedRoute>
            <TeamApp />
          </ProtectedRoute>
        }
      />

      {/* New-hire onboarding (mobile-first) — accessible to any authenticated user */}
      <Route path="/onboarding" element={<ProtectedRoute><ChecklistView /></ProtectedRoute>} />
      <Route path="/onboarding/sign/:itemId" element={<ProtectedRoute><ClickToAgree /></ProtectedRoute>} />
      <Route path="/onboarding/upload/:itemId" element={<ProtectedRoute><DocumentUpload /></ProtectedRoute>} />

      {/* Protected Admin Routes - admins, owners, and managers */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            {isEmployee ? (
              <Navigate to="/team" replace />
            ) : (
              <Layout />
            )}
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="owner-dashboard" element={ownerGate(<OwnerDashboard />)} />
        <Route path="analytics" element={adminGate(<AnalyticsDashboard />)} />
        <Route path="leads" element={<Navigate to="/admin/estimates" replace />} />
        <Route path="dispatch" element={<Dispatch />} />
        <Route path="team-availability" element={<TeamAvailability />} />
        <Route path="todos" element={<Todos />} />
        <Route path="jobs/quick-add" element={<QuickAddJob />} />
        <Route path="clients" element={<Clients />} />
        <Route path="clients/:id" element={<ClientDetail />} />
        <Route path="employees" element={<Employees />} />
        <Route path="employees/:id" element={<EmployeeDetail />} />
        <Route path="employees/:employeeId/onboarding-archive" element={<EmployeeOnboardingArchive />} />
        <Route path="onboarding" element={<ManagerOnboardingDashboard />} />
        <Route path="onboarding/new" element={<NewHireOnboardingForm />} />
        <Route path="onboarding/assign" element={<AssignOnboardingToEmployee />} />
        <Route path="onboarding/assign-policies" element={<AssignPoliciesToEmployee />} />
        <Route path="onboarding/:employeeId" element={<ManagerEmployeeOnboardingView />} />
        <Route path="policy-templates" element={ownerGate(<PolicyTemplateManager />)} />
        <Route path="policy-templates/new" element={ownerGate(<PolicyTemplateEdit />)} />
        <Route path="policy-templates/:templateId" element={ownerGate(<PolicyTemplateEdit />)} />
        <Route path="estimates" element={<Estimates />} />
        <Route path="estimates/new" element={<EstimateNew />} />
        <Route path="estimates/custom" element={<CustomEstimate />} />
        <Route path="estimates/custom/:id" element={<CustomEstimate />} />
        <Route path="estimates/:id" element={<EstimateDetail />} />
        <Route path="estimates/:id/edit" element={<EstimateEdit />} />
        <Route path="estimates/:id/schedule" element={<EstimateSchedule />} />
        <Route path="appointments" element={<OnSiteAppointments />} />
        <Route path="labor" element={<LaborAnalysis />} />
        <Route path="time-cards" element={<TimeCardReport />} />
        <Route path="gusto-report" element={ownerGate(<GustoReport />)} />
        <Route path="attendance" element={<AttendancePoints />} />
        <Route path="complaints-feedback" element={<ComplaintsFeedback />} />
        <Route path="reports" element={adminGate(<Reports />)} />
        <Route path="calendar" element={<CalendarView />} />
        <Route path="messages" element={<Messages />} />
        <Route path="staff-messages" element={<StaffMessages />} />
        <Route path="invoices" element={adminGate(<Invoices />)} />
        <Route path="invoices/new" element={adminGate(<InvoiceNew />)} />
        <Route path="invoices/:id" element={adminGate(<InvoiceDetail />)} />
        <Route path="tips" element={ownerGate(<TipsReport />)} />
        <Route path="expenses" element={ownerGate(<Expenses />)} />
        <Route path="accounts" element={ownerGate(<Accounts />)} />
        <Route path="expense-categories" element={ownerGate(<ExpenseCategories />)} />
        <Route path="bank-import" element={ownerGate(<BankStatementImport />)} />
        <Route path="cashflow" element={ownerGate(<CashflowReport />)} />
        <Route path="quizzes" element={<Quizzes />} />
        <Route path="settings" element={<Settings />} />
      </Route>
      
      {/* Legacy routes - redirect to new /admin paths */}
      <Route path="/dispatch" element={<Navigate to="/admin/dispatch" replace />} />
      <Route path="/dashboard" element={<Navigate to="/admin/dashboard" replace />} />
      <Route path="/clients" element={<Navigate to="/admin/clients" replace />} />
      <Route path="/clients/:id" element={<ClientDetailRedirect />} />
      <Route path="/employees" element={<Navigate to="/admin/employees" replace />} />
      <Route path="/estimates" element={<Navigate to="/admin/estimates" replace />} />
      <Route path="/estimates/:id" element={<EstimateDetailRedirect />} />
      <Route path="/invoices" element={<Navigate to="/admin/invoices" replace />} />
      <Route path="/invoices/new" element={<Navigate to="/admin/invoices/new" replace />} />
      <Route path="/invoices/:id" element={<InvoiceDetailRedirect />} />
      <Route path="/messages" element={<Navigate to="/admin/messages" replace />} />
      <Route path="/settings" element={<Navigate to="/admin/settings" replace />} />

      {/* Catch all - redirect to landing page */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
